import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ConfirmDialog from "../../components/common/ConfirmDialog";
import MyHeader from "../../components/common/MyHeader";
import ArtifactsPreview from "../../components/common/ArtifactsPreview";
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { submitAssessment, getAssessmentByUserAndEvent, updateAssessment } from '../../firebase';
import { getCourseConfig } from '../../services/CourseConfigService';
import { computeRatingWindowEndMs } from '../../utils/ratingWindow';

const WorkAssessment = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, groupMembers } = useAuth();
    const { session, viewMode = false, returnTo = '/artifacts', isReRate = false } = location.state || {};

    const [ratings, setRatings] = useState({
        importance: null,
        quality: null,
        volume: null,
    });
    const [comments, setComments] = useState('');
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [existingAssessment, setExistingAssessment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [modificationReason, setModificationReason] = useState('');
    const [showReasonDialog, setShowReasonDialog] = useState(false);
    const [canEditRatings, setCanEditRatings] = useState(true);
    const [courseId, setCourseId] = useState(null);
    const maxCommentLength = 500;

    const ratingLabels = {
        importance: ['Peripheral', 'Useful', 'Critical', 'Groundbreaking'],
        quality: ['Poor', 'Insufficient', 'Adequate', 'Excellent'],
        volume: ['Small', 'Average', 'Large', 'Huge'],
    };

    useEffect(() => {
        const initialize = async () => {
            if (!session?.eventId || !user?.email) {
                setIsLoading(false);
                return;
            }
            try {
                const cId = session?.courseId || location.state?.courseId;
                if (cId) setCourseId(cId);

                // Load existing assessment and course config in parallel
                const [existing, courseConfig] = await Promise.all([
                    getAssessmentByUserAndEvent(session.eventId, user.email),
                    cId ? getCourseConfig(cId) : Promise.resolve(null),
                ]);

                if (existing) {
                    setExistingAssessment(existing);
                    if (existing.memberRatings && !isReRate) {
                        setRatings({
                            importance: existing.memberRatings.importance?.score || null,
                            quality: existing.memberRatings.quality?.score || null,
                            volume: existing.memberRatings.volume?.score || null,
                        });
                        setComments(existing.memberRatings.comment || '');
                    }
                }

                // Compute edit permission using time-window logic.
                // Re-rate (artifact was updated by the contributor) is always allowed
                // within the time window, even when the instructor has disabled edits.
                const editEnabled = courseConfig?.settings?.editEnabled ?? true;
                if (!existing) {
                    setCanEditRatings(true);
                } else if (!editEnabled && !isReRate) {
                    setCanEditRatings(false);
                } else {
                    const editWindowDays = courseConfig?.settings?.editWindowDays ?? 7;
                    const endMs = computeRatingWindowEndMs({
                        isReRate,
                        artifactLastModifiedAt: session?.artifactLastModifiedAt,
                        submittedAt: existing.submittedAt,
                        reviewDeadline: session?.reviewDeadline,
                        editWindowDays,
                    });
                    setCanEditRatings(endMs == null || Date.now() < endMs);
                }
            } catch (error) {
                console.error('Error initializing work assessment:', error);
                setCanEditRatings(true);
            } finally {
                setIsLoading(false);
            }
        };

        initialize();
    }, [session?.eventId, user?.email, session?.courseId, location.state?.courseId]);

    if (!session) {
        return (
            <>
                <MyHeader />
                <PageWrapper>
                    <BackArrowButton onClick={() => navigate('/artifacts')}>
                        <FiArrowLeft size={16} />
                        <span>Back to Artifacts</span>
                    </BackArrowButton>
                    <ErrorMessage>No session data available. Please go back to Artifacts page.</ErrorMessage>
                </PageWrapper>
            </>
        );
    }

    const handleRatingClick = (category, value) => {
        if (viewMode) return; // Disable clicks in view mode
        if (existingAssessment && !canEditRatings) {
            return;
        }
        setRatings(prev => ({ ...prev, [category]: value }));
    };

    const handleCommentChange = (e) => {
        if (viewMode) return; // Disable changes in view mode
        if (existingAssessment && !canEditRatings) {
            return;
        }
        const value = e.target.value;
        if (value.length <= maxCommentLength) {
            setComments(value);
        }
    };

    const handleSubmit = async () => {
        if (!ratings.importance || !ratings.quality || !ratings.volume) {
            alert('Please provide ratings for all criteria.');
            return;
        }

        if (existingAssessment && !canEditRatings) {
            alert('Rating modifications have been disabled by the instructor. You cannot update your assessment.');
            return;
        }

        if (existingAssessment && canEditRatings) {
            if (isReRate) {
                // Artifact changed — skip reason dialog
                await submitRating('Artifact was updated by contributor');
                return;
            }
            setShowReasonDialog(true);
            return;
        }

        await submitRating();
    };

    const submitRating = async (reason = null) => {
        setIsSubmitting(true);

        try {
            const memberRatings = {
                importance: {
                    score: ratings.importance,
                    label: ratingLabels.importance[ratings.importance - 1],
                },
                quality: {
                    score: ratings.quality,
                    label: ratingLabels.quality[ratings.quality - 1],
                },
                volume: {
                    score: ratings.volume,
                    label: ratingLabels.volume[ratings.volume - 1],
                },
                comment: comments,
            };

            if (existingAssessment) {
                // Update with history tracking
                await updateAssessment(existingAssessment.id, memberRatings, {
                    modificationReason: reason,
                    context: {
                        eventId: session.eventId,
                        raterEmail: user.email,
                        raterName: user.name || user.displayName || user.email,
                        courseId: courseId,
                        groupId: session.groupId,
                        eventTitle: session.title || 'Untitled Activity',
                        eventDate: session.date || 'N/A',
                    },
                    trackHistory: true,
                });
            } else {
                await submitAssessment(session.eventId, user.email, memberRatings);
            }
            navigate(returnTo);
        } catch (error) {
            console.error('Error submitting assessment:', error);
            alert('Failed to submit assessment. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReasonSubmit = () => {
        if (!modificationReason.trim()) {
            alert('Please provide a reason for modifying your rating.');
            return;
        }
        setShowReasonDialog(false);
        submitRating(modificationReason);
    };

    const handleCancel = () => {
        if (viewMode) {
            navigate(returnTo);
            return;
        }
        setShowCancelDialog(true);
    };

    const confirmCancel = () => {
        setShowCancelDialog(false);
        navigate(returnTo);
    };

    const contributors = session.contributors || [];
    const contributorUsers = groupMembers.filter(member => {
        return contributors.some(contributor => {
            const contributorEmail = typeof contributor === 'string' ? contributor : contributor?.email;
            return contributorEmail === member.email;
        });
    });

    const artifacts = [];
    if (session.prMeta) artifacts.push({ type: 'PR', title: session.prMeta.title });
    if (session.files?.length) artifacts.push(...session.files.map(f => ({ type: 'File', name: f.name })));
    if (session.googleDocs?.length) artifacts.push(...session.googleDocs.map(d => ({ type: 'Google Doc', name: d.name || d.meta?.title })));
    if (session.figmaFiles?.length) artifacts.push(...session.figmaFiles.map(f => ({ type: 'Figma', name: f.name || f.meta?.fileName })));


    const displayDate = session.date || 'N/A';
    const displayTitle = session.title || 'Untitled Activity';

    return (
        <>
            <MyHeader />
            <PageWrapper>
                <ContentCard>
                    <BackArrowButton onClick={() => viewMode ? navigate(returnTo) : setShowCancelDialog(true)}>
                        <FiArrowLeft size={16} />
                        <span>Back to Artifacts</span>
                    </BackArrowButton>
                    <Title>Work Assessment</Title>

                    <SessionInfo>
                        <InfoRow>
                            <InfoLabel>Activity:</InfoLabel>
                            <InfoValue>{displayTitle}</InfoValue>
                        </InfoRow>
                        
                        <InfoRow>
                            <InfoLabel>Submitted by:</InfoLabel>
                            <ContributorsWrapper>
                                {contributorUsers.length > 0 ? (
                                    contributorUsers.map((member, index) => (
                                        <Avatar 
                                            key={index}
                                            $color={member.color || '#cccccc'}
                                            title={member.name || member.email}
                                        >
                                            {member.initial || (member.name || member.email).charAt(0).toUpperCase()}
                                        </Avatar>
                                    ))
                                ) : (
                                    <span style={{ fontSize: '14px', color: '#6b7280' }}>No contributors</span>
                                )}
                            </ContributorsWrapper>
                        </InfoRow>

                        <CompactInfoRow>
                            <CompactInfoItem>
                                <InfoLabel>Date:</InfoLabel>
                                <InfoValue>{displayDate}</InfoValue>
                            </CompactInfoItem>

                            {session.startTime && session.endTime && (
                                <CompactInfoItem>
                                    <InfoLabel>Time:</InfoLabel>
                                    <InfoValue>{session.startTime} - {session.endTime}</InfoValue>
                                </CompactInfoItem>
                            )}

                            {artifacts.length > 0 && (
                                <CompactInfoItem>
                                    <InfoLabel>Artifacts:</InfoLabel>
                                    <InfoValue>{artifacts.length} item{artifacts.length !== 1 ? 's' : ''}</InfoValue>
                                </CompactInfoItem>
                            )}
                        </CompactInfoRow>

                        <StatusBadgeWrapper>
                            <StatusBadge $isSubmitted={!!existingAssessment} $isReRate={isReRate}>
                                {isReRate ? 'Artifact Changed' : existingAssessment ? 'Already Submitted' : 'Pending Review'}
                            </StatusBadge>
                        </StatusBadgeWrapper>
                    </SessionInfo>

                    {existingAssessment && !canEditRatings && (
                        <WarningBanner>
                            <WarningText>
                                Rating modifications have been disabled by the instructor.
                                You can view your submission but cannot make changes.
                            </WarningText>
                        </WarningBanner>
                    )}

                    {existingAssessment && canEditRatings && !viewMode && isReRate && (
                        <InfoBanner $isReRate>
                            <InfoText>
                                The artifact for this activity has been updated. Please review the new artifact and re-submit your rating.
                            </InfoText>
                        </InfoBanner>
                    )}

                    {existingAssessment && canEditRatings && !viewMode && !isReRate && (
                        <InfoBanner>
                            <InfoText>
                                You are updating your previous assessment. You will be asked to provide a reason for the modification.
                            </InfoText>
                        </InfoBanner>
                    )}

                    <ArtifactsPreview session={session} />

                    <SectionTitle>Assessment Criteria</SectionTitle>
                    <Instruction>Rate each aspect on a scale of 1-4</Instruction>

                    <CriteriaSection>
                        <CriteriaTitle>Nature</CriteriaTitle>
                        <CriteriaQuestion>What is the nature of the contribution?</CriteriaQuestion>
                        
                        <RatingOptions>
                            <RatingOption
                                $isSelected={ratings.importance === 1}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('importance', 1)}
                            >
                                <RadioCircle $isSelected={ratings.importance === 1} />
                                <OptionLabel>
                                    Peripheral: addresses<br />auxiliary material
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.importance === 2}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('importance', 2)}
                            >
                                <RadioCircle $isSelected={ratings.importance === 2} />
                                <OptionLabel>
                                    Useful: supports<br />the main concept/<br />solution
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.importance === 3}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('importance', 3)}
                            >
                                <RadioCircle $isSelected={ratings.importance === 3} />
                                <OptionLabel>
                                    Critical: moves the<br />main concept/<br />solution forward
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.importance === 4}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('importance', 4)}
                            >
                                <RadioCircle $isSelected={ratings.importance === 4} />
                                <OptionLabel>
                                    Groundbreaking:<br />introduces (or reshapes)<br />the main concept/solution
                                </OptionLabel>
                            </RatingOption>
                        </RatingOptions>
                    </CriteriaSection>

                    <CriteriaSection>
                        <CriteriaTitle>Quality</CriteriaTitle>
                        <CriteriaQuestion>What is the quality of the contribution?</CriteriaQuestion>
                        
                        <RatingOptions>
                            <RatingOption
                                $isSelected={ratings.quality === 1}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('quality', 1)}
                            >
                                <RadioCircle $isSelected={ratings.quality === 1} />
                                <OptionLabel>
                                    Poor: most of it has to<br />be redone
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.quality === 2}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('quality', 2)}
                            >
                                <RadioCircle $isSelected={ratings.quality === 2} />
                                <OptionLabel>
                                    Insufficient: aspects<br />need to be reworked
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.quality === 3}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('quality', 3)}
                            >
                                <RadioCircle $isSelected={ratings.quality === 3} />
                                <OptionLabel>
                                    Adequate: good, but<br />some polishing still<br />needed
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.quality === 4}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('quality', 4)}
                            >
                                <RadioCircle $isSelected={ratings.quality === 4} />
                                <OptionLabel>
                                    Excellent: nothing (or very<br />little) to do
                                </OptionLabel>
                            </RatingOption>
                        </RatingOptions>
                    </CriteriaSection>

                    <CriteriaSection>
                        <CriteriaTitle>Volume</CriteriaTitle>
                        <CriteriaQuestion>What is the size of the contribution?</CriteriaQuestion>
                        
                        <RatingOptions>
                            <RatingOption
                                $isSelected={ratings.volume === 1}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('volume', 1)}
                            >
                                <RadioCircle $isSelected={ratings.volume === 1} />
                                <OptionLabel>
                                    Small: some<br />tinkering here<br />or there
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.volume === 2}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('volume', 2)}
                            >
                                <RadioCircle $isSelected={ratings.volume === 2} />
                                <OptionLabel>
                                    Average: a few<br />chunks of work
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.volume === 3}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('volume', 3)}
                            >
                                <RadioCircle $isSelected={ratings.volume === 3} />
                                <OptionLabel>
                                    Large: healthy<br />step forward
                                </OptionLabel>
                            </RatingOption>
                            <RatingOption
                                $isSelected={ratings.volume === 4}
                                $disabled={viewMode}
                                onClick={() => handleRatingClick('volume', 4)}
                            >
                                <RadioCircle $isSelected={ratings.volume === 4} />
                                <OptionLabel>
                                    Huge: fantastic<br />progress
                                </OptionLabel>
                            </RatingOption>
                        </RatingOptions>
                    </CriteriaSection>

                    <CommentSection>
                        <SectionTitle>Comments</SectionTitle>
                        <Instruction>Provide additional feedback (optional)</Instruction>
                        <CommentTextarea
                            value={comments}
                            onChange={handleCommentChange}
                            placeholder="Great work on the final submission! The document is well-structured and covers all required sections. The design process is clearly documented with excellent visuals."
                            disabled={viewMode}
                        />
                        <CharacterCount>
                            {comments.length}/{maxCommentLength}
                        </CharacterCount>
                    </CommentSection>

                    <ButtonRow>
                        <CancelButton onClick={handleCancel} disabled={isSubmitting}>
                            {viewMode ? 'Return' : 'Cancel'}
                        </CancelButton>
                        {!viewMode && (
                            <SubmitButton
                                onClick={handleSubmit}
                                disabled={
                                    isSubmitting ||
                                    isLoading ||
                                    (existingAssessment && !canEditRatings)
                                }
                            >
                                {isSubmitting ? 'Submitting...' :
                                 existingAssessment ?
                                    (isReRate ? 'Resubmit Assessment' : canEditRatings ? 'Update Assessment' : 'Editing Disabled') :
                                 'Submit Assessment'}
                            </SubmitButton>
                        )}
                    </ButtonRow>
                </ContentCard>

                {showCancelDialog && (
                    <ConfirmDialog
                        title="Cancel Assessment?"
                        message="Are you sure you want to cancel? Unsaved changes will be lost."
                        confirmText="Yes, Cancel"
                        cancelText="Go Back"
                        variant="danger"
                        onConfirm={confirmCancel}
                        onCancel={() => setShowCancelDialog(false)}
                    />
                )}

                {showReasonDialog && (
                    <DialogOverlay onClick={() => setShowReasonDialog(false)}>
                        <DialogBox onClick={(e) => e.stopPropagation()}>
                            <DialogTitle>Reason for Modification</DialogTitle>
                            <DialogMessage>
                                Please explain why you are updating your assessment.
                                This helps maintain transparency in the peer review process.
                            </DialogMessage>
                            <ReasonTextarea
                                value={modificationReason}
                                onChange={(e) => setModificationReason(e.target.value)}
                                placeholder="Example: I initially underestimated the importance of this contribution after reviewing the final results..."
                                maxLength={500}
                                autoFocus
                            />
                            <CharacterCount>
                                {modificationReason.length}/500
                            </CharacterCount>
                            <DialogButtons>
                                <DialogCancelBtn onClick={() => setShowReasonDialog(false)}>
                                    Cancel
                                </DialogCancelBtn>
                                <DialogConfirmBtn onClick={handleReasonSubmit}>
                                    Submit Update
                                </DialogConfirmBtn>
                            </DialogButtons>
                        </DialogBox>
                    </DialogOverlay>
                )}
            </PageWrapper>
        </>
    );
};

const DialogOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const DialogBox = styled.div`
    background: white;
    border-radius: 14px;
    padding: 28px;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e7eb;
`;

const DialogTitle = styled.h3`
    margin: 0 0 12px 0;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
`;

const DialogMessage = styled.p`
    margin: 0 0 24px 0;
    font-size: 14px;
    color: #6b7280;
    line-height: 1.5;
`;

const DialogButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`;

const DialogCancelBtn = styled.button`
    height: 40px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid rgba(147, 51, 234, 0.2);
    background: rgba(147, 51, 234, 0.08);
    color: #9333ea;
    transition: all 0.15s;

    &:hover {
        background: rgba(147, 51, 234, 0.15);
    }
`;

const DialogConfirmBtn = styled.button`
    height: 40px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    background: #dc2626;
    color: white;
    transition: all 0.15s;
    box-shadow: 0 4px 10px rgba(220, 38, 38, 0.25);

    &:hover {
        filter: brightness(1.05);
    }
`;

const PageWrapper = styled.div`
    background-color: #fff;
    min-height: 100vh;
    padding: 30px 20px 60px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
`;

const ContentCard = styled.div`
    background: white;
    border-radius: 14px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    padding: 32px;
    max-width: 900px;
    width: 100%;
`;

const Title = styled.h1`
    font-size: 24px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 24px;
    text-align: center;
`;

const SessionInfo = styled.div`
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 20px 24px;
    margin-bottom: 28px;
    position: relative;
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const CompactInfoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CompactInfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 32px;
    margin-bottom: 12px;
    flex-wrap: wrap;
`;

const InfoLabel = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    min-width: 140px;
`;

const InfoValue = styled.div`
    font-size: 15px;
    font-weight: 400;
    color: #111827;
    flex: 1;
    text-align: left;
    display: flex;
    align-items: center;
`;

const ContributorsWrapper = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex: 1;
`;

const StatusBadgeWrapper = styled.div`
    position: absolute;
    top: 20px;
    right: 24px;
`;

const StatusBadge = styled.span`
    background-color: ${props => props.$isReRate ? '#fef3c7' : props.$isSubmitted ? '#d1fae5' : '#fef3c7'};
    color: ${props => props.$isReRate ? '#92400e' : props.$isSubmitted ? '#065f46' : '#92400e'};
    padding: 6px 14px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    border: 1px solid ${props => props.$isReRate ? '#fcd34d' : props.$isSubmitted ? '#a7f3d0' : '#fde68a'};
`;

const Avatar = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${props => props.$color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    position: relative;
    transition: transform 0.2s;
    
    /* Tooltip - hidden by default */
    &::after {
        content: attr(title);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%);
        background: white;
        color: #111827;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 100;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition: opacity 0.2s ease, visibility 0.2s ease;
    }
    
    /* Tooltip arrow */
    &::before {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #d1d5db;
        z-index: 99;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.2s ease, visibility 0.2s ease;
    }
    
    /* Show tooltip on hover when title exists */
    &[title]:hover::after,
    &[title]:hover::before {
        opacity: 1;
        visibility: visible;
    }
    
    &:hover {
        transform: translateY(-2px);
    }
`;

const SectionTitle = styled.h2`
    font-size: 17px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 6px;
    text-align: left;
`;

const Instruction = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 20px;
    text-align: left;
`;

const CriteriaSection = styled.div`
    margin-bottom: 32px;
    padding-bottom: 28px;
    border-bottom: 1px solid #e5e7eb;
    
    &:last-of-type {
        border-bottom: none;
    }
`;

const CriteriaTitle = styled.h3`
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 3px;
    text-align: left;
`;

const CriteriaQuestion = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 16px;
    text-align: left;
`;

const RatingOptions = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
`;

const RatingOption = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 10px;
    border: 2px solid ${props => props.$isSelected ? '#9333ea' : '#e5e7eb'};
    border-radius: 12px;
    transition: all 0.15s;
    background-color: ${props => props.$isSelected ? '#f5f3ff' : 'white'};
    min-height: 110px;
    cursor: ${props => props.$disabled ? 'default' : 'pointer'};

    &:hover {
        border-color: ${props => props.$disabled ? (props.$isSelected ? '#9333ea' : '#e5e7eb') : (props.$isSelected ? '#9333ea' : '#9ca3af')};
        background-color: ${props => props.$isSelected ? '#f5f3ff' : 'white'};
    }
`;

const RadioCircle = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid ${props => props.$isSelected ? '#9333ea' : '#d1d5db'};
    background-color: ${props => props.$isSelected ? '#9333ea' : 'white'};
    margin-bottom: 10px;
    position: relative;
    transition: all 0.15s;
    flex-shrink: 0;

    ${props => props.$isSelected && `
        &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: white;
        }
    `}
`;

const OptionLabel = styled.div`
    text-align: center;
    font-size: 12px;
    color: #374151;
    line-height: 1.4;
`;

const CommentSection = styled.div`
    margin-bottom: 28px;
`;

const CommentTextarea = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 14px;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 13px;
    font-family: inherit;
    color: #111827;
    background-color: white;
    resize: vertical;
    transition: border-color 0.15s;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const CharacterCount = styled.div`
    text-align: right;
    font-size: 11px;
    color: #6b7280;
    margin-top: 6px;
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
`;

const BackArrowButton = styled.button`
    display: flex;
    align-items: center;
    width: fit-content;
    gap: 8px;
    background-color: #fafafc;
    color: #333333;
    border: 3px solid rgba(0, 0, 0, 0.04);
    border-radius: 11px;
    padding: 8px 14px;
    font-family: "SF Pro Text", system-ui, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.29;
    letter-spacing: -0.224px;
    cursor: pointer;
    transition: transform 0.1s ease-in-out;
    margin-bottom: 16px;

    &:active {
        transform: scale(0.95);
    }
`;

const CancelButton = styled.button`
    height: 48px;
    padding: 0 18px;
    display: flex;
    align-items: center;
    border: 1px solid rgba(147, 51, 234, 0.2);
    border-radius: 12px;
    background: rgba(147, 51, 234, 0.08);
    color: #9333ea;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: rgba(147, 51, 234, 0.15);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const SubmitButton = styled.button`
    height: 48px;
    padding: 0 24px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 12px;
    background: #9333ea;
    color: white;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(147, 51, 234, 0.25);
    transition: all 0.15s;

    &:hover:not(:disabled) {
        filter: brightness(1.05);
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const ErrorMessage = styled.div`
    color: #dc2626;
    font-size: 16px;
    margin-bottom: 20px;
`;

const WarningBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: #fef3c7;
    border: 1px solid #fde68a;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 20px;
`;

const WarningIcon = styled.span`
    font-size: 24px;
    flex-shrink: 0;
`;

const WarningText = styled.p`
    margin: 0;
    font-size: 14px;
    color: #92400e;
    font-weight: 500;
`;

const InfoBanner = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: ${props => props.$isReRate ? '#fef3c7' : '#f5f3ff'};
    border: 1px solid ${props => props.$isReRate ? '#fcd34d' : '#ddd6fe'};
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 20px;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 14px;
    color: #5b21b6;
    font-weight: 500;
`;

const ReasonTextarea = styled.textarea`
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #111827;
    resize: vertical;
    margin-bottom: 8px;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

export default WorkAssessment;
